import {
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import {
  EnhancedPlan,
  PlanSubtypes,
  PlanTypeBE,
} from '@mm/state/interfaces/subscription';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { includes } from 'lodash';
import React, { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import Popover from 'react-tiny-popover';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useActivateSmsRedirectCallback } from '../../../../../../../../common/hooks/use-activate-sms-redirect-callback';
import {
  BillingTypeLevelDict,
  NEW_PRICING_FILTERING_LEVEL,
} from '../../../../../../../../common/hooks/use-match-billing-plan';
import { BillingPannelContext } from '../../../../BillingPanel';
import './UpgradeOrDowngradeButton.pcss';

type Props = {
  color?: ButtonColor;
  page: EnhancedBillingPage;
  downgradePlansIds: number[];
  upgradePlansIds: number[];
  disabled?: boolean;
};

const getCanUpgradeToPlans = (
  page: EnhancedBillingPage,
  originalPlans: EnhancedPlan[],
  upgradePlansIds: number[],
  november2021Pricing: boolean,
  excludeInstachamp: boolean,
) => {
  const plans = november2021Pricing
    ? originalPlans.filter(p => {
        const billingTypeLevel = BillingTypeLevelDict[p.pageBillingType];
        if (!billingTypeLevel) return false;
        return billingTypeLevel >= NEW_PRICING_FILTERING_LEVEL;
      })
    : originalPlans;

  if (page.pageBillingType === PageBillingType.Free) {
    return plans.filter(plan => {
      return (
        plan.canUpgradeFromFree &&
        [PlanTypeBE.team, PlanTypeBE.teamSpecial].indexOf(plan.planType) ===
          -1 &&
        !plan.planSubtypes.includes(PlanSubtypes.instachamp)
      );
    });
  }
  if (excludeInstachamp) {
    return plans.filter(
      plan =>
        includes(upgradePlansIds, plan.id) &&
        !plan.planSubtypes.includes(PlanSubtypes.instachamp),
    );
  }

  return plans.filter(plan => includes(upgradePlansIds, plan.id));
};

const UpgradeOrDowngradeButton = (props: Props) => {
  const { upgradePlansIds, page, downgradePlansIds, disabled } = props;

  const plans = useSelector(enhancedPlansSelector);
  const company = useSelector(activeCompanySelector);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const plansWithoutYearly = plans.filter(p => p.billingPeriod !== 'year');

  const { november2021Pricing = false } = useSelector(
    combinedFeatureFlagsSelector,
  );
  const canUpgradeToPlans = getCanUpgradeToPlans(
    page,
    plansWithoutYearly,
    upgradePlansIds,
    true,
    false,
  );
  const canDowngradeToPlans = getCanUpgradeToPlans(
    page,
    plansWithoutYearly,
    downgradePlansIds,
    november2021Pricing,
    true,
  );
  const color = props.color || ButtonColor.blue;

  const { openBillingModal } = useModalActions();
  const { landmineOpenId } = useContext(BillingPannelContext);
  const closeDropdown = useCallback(() => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    }
  }, [dropdownOpen, setDropdownOpen]);

  const onSuccessBilling = useActivateSmsRedirectCallback();

  const createOrUpgradeSubscription = (plan: EnhancedPlan) => {
    closeDropdown();
    if (page.pageBillingType === PageBillingType.Free) {
      openBillingModal({
        facebookPage: page,
        plan,
        landmineOpenId,
        stayOnPage: false,
        onSuccessBilling,
      });
    } else {
      const upgradeFromSubscriptionId = page.subscription
        ? page.subscription.id
        : undefined;

      if (upgradeFromSubscriptionId) {
        openBillingModal({
          facebookPage: page,
          plan,
          upgradeFromSubscriptionId,
          landmineOpenId,
          subscription: page.subscription,
          stayOnPage: false,
        });
      }
    }
  };

  if (canDowngradeToPlans.length && company) {
    if (canDowngradeToPlans.length === 1) {
      const plan = canDowngradeToPlans[0];
      const subscriptionId = page.subscription
        ? page.subscription.id
        : undefined;

      if (subscriptionId && company) {
        const onClickDowngrade = () => {
          const downgradeFromSubscriptionId = page.subscription
            ? page.subscription.id
            : undefined;

          if (downgradeFromSubscriptionId) {
            openBillingModal({
              facebookPage: page,
              plan,
              downgradeFromSubscriptionId,
              landmineOpenId,
              subscription: page.subscription,
              stayOnPage: false,
            });
          }
        };

        return (
          <div className="upgrade-or-downgrade-button">
            <PrimaryButton
              onClick={onClickDowngrade}
              color={color}
              disabled={disabled}
            >
              Downgrade to {plan.shortName}
            </PrimaryButton>
          </div>
        );
      }
    }
  }

  if (canUpgradeToPlans.length) {
    if (canUpgradeToPlans.length === 1) {
      const plan = canUpgradeToPlans[0];

      return (
        <div className="upgrade-or-downgrade-button">
          <PrimaryButton
            color={color}
            onClick={() => createOrUpgradeSubscription(plan)}
            disabled={disabled}
          >
            Upgrade to {plan.shortName}
          </PrimaryButton>
        </div>
      );
    }

    return (
      <div className="upgrade-or-downgrade-button">
        <Popover
          disableReposition
          isOpen={dropdownOpen}
          onClickOutside={closeDropdown}
          align="start"
          position="bottom"
          content={() => (
            <div className="upgrade-or-downgrade-button__dropdown">
              {canUpgradeToPlans.map(plan => (
                <div
                  key={plan.planCode}
                  onClick={() => createOrUpgradeSubscription(plan)}
                  className="upgrade-or-downgrade-button__dropdown-item"
                >
                  {plan.shortName}
                </div>
              ))}
            </div>
          )}
        >
          <PrimaryButton
            color={color}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            disabled={disabled}
          >
            Upgrade
          </PrimaryButton>
        </Popover>
      </div>
    );
  }

  return null;
};

export default UpgradeOrDowngradeButton;
